import dayjs from "dayjs";

import { Finding, FindingSeverity, OpenStates } from "stores/FindingStore";

import { GenericObject } from "utility/misc";

// -----------------------------------------------------------------------------

const ChartType: GenericObject = {
  [FindingSeverity.Critical]: "critical",
  [FindingSeverity.High]: "high",
  [FindingSeverity.Medium]: "medium",
  [FindingSeverity.Low]: "low",
};

const keys: Array<"critical" | "high" | "medium" | "low" | "other"> = [
  "critical",
  "high",
  "medium",
  "low",
  "other",
];

interface CumulativeCounts {
  critical: number;
  high: number;
  medium: number;
  low: number;
  other: number;
}

interface CumulativeData {
  critical: DataByTime;
  high: DataByTime;
  medium: DataByTime;
  low: DataByTime;
  other: DataByTime;
}

interface DataByTime {
  [key: number]: number;
}

// -----------------------------------------------------------------------------

// This builds a timeline chart for the severity types from the first finding
// to the (user's) current date

export default function getChartData(findings: Finding[] = []) {
  if (findings.length < 1) {
    return {
      critical: [],
      high: [],
      medium: [],
      low: [],
      other: [],
    };
  }

  //
  const chartData: GenericObject = {
    critical: {},
    high: {},
    medium: {},
    low: {},
    other: {},
  };

  const cumulativeData: CumulativeData = {
    critical: {},
    high: {},
    medium: {},
    low: {},
    other: {},
  };

  //
  const cumulative: CumulativeCounts = {
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    other: 0,
  };

  (findings || []).forEach((finding) => {
    const chartKey: "critical" | "high" | "medium" | "low" | "other" =
      ChartType[finding.severity as any] || "other";

    //
    cumulative[chartKey] += 1;

    //
    const created = getUTCDate(finding.create_time);
    const createdIndex = created.getTime();

    cumulativeData[chartKey][createdIndex] =
      (cumulativeData[chartKey][createdIndex] || 0) + 1;

    //
    if (!OpenStates.includes(finding.state) && finding.resolved_time != null) {
      cumulative[chartKey] -= 1;

      //
      const resolved = getUTCDate(finding.resolved_time);
      const resolvedIndex = resolved.getTime();

      cumulativeData[chartKey][resolvedIndex] =
        (cumulativeData[chartKey][resolvedIndex] || 0) - 1;
    }
  });

  //
  const firstDate = getUTCDate(new Date(findings[0].create_time));
  const today = getUTCDate(new Date()).getTime();

  let currentDate = firstDate;

  const lastValue: CumulativeCounts = {
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    other: 0,
  };

  do {
    const date = new Date(currentDate);
    const index = date.getTime();

    keys.forEach((key) => {
      if (chartData[key][index]) {
        return;
      }

      const adjustment = cumulativeData[key][index] || 0;
      lastValue[key] += adjustment;

      chartData[key][index] = { x: date, y: lastValue[key] };
    });

    currentDate = dayjs(currentDate).add(1, "d").toDate();
  } while (!dayjs(currentDate).isAfter(today));

  //
  return Object.entries(chartData).reduce<GenericObject>(
    (obj: GenericObject, [key, value]: [string, GenericObject]) => {
      obj[key] = Object.values(value);

      return obj;
    },
    {}
  );
}

function getUTCDate(date: Date) {
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    0,
    0,
    0,
    0
  );
}
